import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, buildUrl } from "@shared/routes";
import type { InsertTicket, InsertTicketNote } from "@shared/schema";
import { authFetch } from "@/lib/authFetch";

export function useTickets() {
  return useQuery({
    queryKey: [api.tickets.list.path],
    queryFn: async () => {
      const res = await authFetch(api.tickets.list.path);
      if (!res.ok) throw new Error("Failed to fetch tickets");
      return res.json();
    },
  });
}

export function useTicket(id: string | null) {
  return useQuery({
    queryKey: [api.tickets.get.path, id],
    queryFn: async () => {
      const url = buildUrl(api.tickets.get.path, { id: id! });
      const res = await authFetch(url);
      if (!res.ok) throw new Error("Failed to fetch ticket");
      return res.json();
    },
    enabled: !!id,
  });
}

export function useCreateTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InsertTicket) => {
      const res = await authFetch(api.tickets.create.path, {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to create ticket");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.tickets.list.path] }),
  });
}

export function useUpdateTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & Partial<InsertTicket>) => {
      const url = buildUrl(api.tickets.update.path, { id });
      const res = await authFetch(url, {
        method: "PUT",
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to update ticket");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.tickets.list.path] });
      queryClient.invalidateQueries({ queryKey: [api.tickets.get.path] });
    },
  });
}

export function useDeleteTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const url = buildUrl(api.tickets.delete.path, { id });
      const res = await authFetch(url, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete ticket");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.tickets.list.path] }),
  });
}

export function useArchiveTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/tickets/${id}/archive`, { method: "PUT" });
      if (!res.ok) throw new Error("Failed to archive ticket");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.tickets.list.path] });
      queryClient.invalidateQueries({ queryKey: [api.tickets.get.path] });
    },
  });
}

export function useUnarchiveTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/tickets/${id}/unarchive`, { method: "PUT" });
      if (!res.ok) throw new Error("Failed to unarchive ticket");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.tickets.list.path] });
      queryClient.invalidateQueries({ queryKey: [api.tickets.get.path] });
    },
  });
}

export function useTicketsByClient(clientId: string | null) {
  return useQuery({
    queryKey: [api.tickets.byClient.path, clientId],
    queryFn: async () => {
      const url = buildUrl(api.tickets.byClient.path, { clientId: clientId! });
      const res = await authFetch(url);
      if (!res.ok) throw new Error("Failed to fetch client tickets");
      return res.json();
    },
    enabled: !!clientId,
  });
}

export function useTicketsByReseller(resellerId: string | null) {
  return useQuery({
    queryKey: [api.tickets.byReseller.path, resellerId],
    queryFn: async () => {
      const url = buildUrl(api.tickets.byReseller.path, { resellerId: resellerId! });
      const res = await authFetch(url);
      if (!res.ok) throw new Error("Failed to fetch reseller tickets");
      return res.json();
    },
    enabled: !!resellerId,
  });
}

export function useTicketNotes(ticketId: string | null) {
  return useQuery({
    queryKey: [api.tickets.notes.list.path, ticketId],
    queryFn: async () => {
      const url = buildUrl(api.tickets.notes.list.path, { id: ticketId! });
      const res = await authFetch(url);
      if (!res.ok) throw new Error("Failed to fetch ticket notes");
      return res.json();
    },
    enabled: !!ticketId,
  });
}

export function useCreateTicketNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ticketId, content }: { ticketId: string; content: string }) => {
      const url = buildUrl(api.tickets.notes.create.path, { id: ticketId });
      const res = await authFetch(url, {
        method: "POST",
        body: JSON.stringify({ content }),
      });
      if (!res.ok) throw new Error("Failed to create note");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.tickets.notes.list.path] }),
  });
}

export function useTicketMissions(ticketId: string | null) {
  return useQuery({
    queryKey: [api.tickets.missions.list.path, ticketId],
    queryFn: async () => {
      const url = buildUrl(api.tickets.missions.list.path, { id: ticketId! });
      const res = await authFetch(url);
      if (!res.ok) throw new Error("Failed to fetch ticket missions");
      return res.json();
    },
    enabled: !!ticketId,
  });
}

export function useCreateTicketMission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ticketId, missionId }: { ticketId: string; missionId: string }) => {
      const url = buildUrl(api.tickets.missions.create.path, { id: ticketId });
      const res = await authFetch(url, {
        method: "POST",
        body: JSON.stringify({ missionId }),
      });
      if (!res.ok) throw new Error("Failed to link mission");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.tickets.missions.list.path] });
      queryClient.invalidateQueries({ queryKey: [api.missions.list.path] });
    },
  });
}

export function useTicketMessages(ticketId: string | null) {
  return useQuery({
    queryKey: ["/api/tickets/messages", ticketId],
    queryFn: async () => {
      const res = await authFetch(`/api/tickets/${ticketId}/messages`);
      if (!res.ok) throw new Error("Failed to fetch messages");
      return res.json();
    },
    enabled: !!ticketId,
  });
}

export function useCreateTicketMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ticketId, auteur, auteurId, contenu, mentionedUserIds }: { ticketId: string; auteur: string; auteurId?: string; contenu: string; mentionedUserIds?: string[] }) => {
      const res = await authFetch(`/api/tickets/${ticketId}/messages`, {
        method: "POST",
        body: JSON.stringify({ auteur, auteurId, contenu, mentionedUserIds: mentionedUserIds?.length ? mentionedUserIds : undefined }),
      });
      if (!res.ok) throw new Error("Failed to create message");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/tickets/messages"] }),
  });
}

export function useUpdateTicketMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ticketId, msgId, contenu, mentionedUserIds }: { ticketId: string; msgId: string; contenu: string; mentionedUserIds?: string[] }) => {
      const res = await authFetch(`/api/tickets/${ticketId}/messages/${msgId}`, {
        method: "PATCH",
        body: JSON.stringify({ contenu, mentionedUserIds: mentionedUserIds?.length ? mentionedUserIds : undefined }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to update message");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/tickets/messages"] }),
  });
}

export function useTicketPieces(ticketId: string | null) {
  return useQuery({
    queryKey: ["/api/tickets/pieces", ticketId],
    queryFn: async () => {
      const res = await authFetch(`/api/tickets/${ticketId}/pieces`);
      if (!res.ok) throw new Error("Failed to fetch pieces");
      return res.json();
    },
    enabled: !!ticketId,
  });
}

export function useCreateTicketPiece() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ticketId, produitId, quantite, sousGarantie, note }: { ticketId: string; produitId: string; quantite?: number; sousGarantie?: boolean; note?: string }) => {
      const res = await authFetch(`/api/tickets/${ticketId}/pieces`, {
        method: "POST",
        body: JSON.stringify({ produitId, quantite, sousGarantie, note }),
      });
      if (!res.ok) throw new Error("Failed to add piece");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/tickets/pieces"] }),
  });
}

export function useUpdateTicketPiece() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; quantite?: number; sousGarantie?: boolean; note?: string }) => {
      const res = await authFetch(`/api/ticket-pieces/${id}`, {
        method: "PUT",
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error("Failed to update piece");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/tickets/pieces"] }),
  });
}

export function useDeleteTicketPiece() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/ticket-pieces/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete piece");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/tickets/pieces"] }),
  });
}

// ── Ticket Media ──────────────────────────────────────────────────────────

export function useTicketMedia(ticketId: string | null) {
  return useQuery({
    queryKey: ["/api/tickets/media", ticketId],
    queryFn: async () => {
      const res = await authFetch(`/api/tickets/${ticketId}/media`);
      if (!res.ok) throw new Error("Failed to fetch media");
      return res.json();
    },
    enabled: !!ticketId,
  });
}

export function useDeleteTicketMedia(ticketId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (mediaId: string) => {
      const res = await authFetch(`/api/tickets/${ticketId}/media/${mediaId}`, { method: "DELETE" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to delete media");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/tickets/media", ticketId] }),
  });
}
